// Setup Wizard Types

export type MerchantType = 'regular' | 'agent';

export interface MerchantData {
  name: string;
  description: string;
  supportEmail: string;
  restrictedIps: string;
  isBatchPostback: 'yes' | 'no';
  type: MerchantType;
}

// Fee Data for new fee group creation (matches existing form)
export interface FeeDataItem {
  type: string;
  asset: string;
  asset_type: string;
  display_name: string;
  decimal: number;
  fixed: string;
  percentage: string;
  max_amount: string;
  min_amount: string;
  is_crypto: boolean;
}

export interface NewFeeGroupData {
  name: string;
  description: string;
  fee_data: FeeDataItem[];
}

// Simpler fee group data for agent assignments (quick inline creation)
export interface SimpleNewFeeGroup {
  name: string;
  fixed: string;
  percentage: string;
}

export interface FeeGroupSelection {
  mode: 'existing' | 'new';
  existingFeeGroupId?: string;
  existingFeeGroupName?: string;
  newFeeGroup?: NewFeeGroupData | SimpleNewFeeGroup;
  createdId?: string;
}

// Type guard to check if newFeeGroup is the full form data
export const isFullFeeGroupData = (data: NewFeeGroupData | SimpleNewFeeGroup | undefined): data is NewFeeGroupData => {
  return data !== undefined && 'fee_data' in data;
};

// Type guard to check if newFeeGroup is the simple form data
export const isSimpleFeeGroupData = (data: NewFeeGroupData | SimpleNewFeeGroup | undefined): data is SimpleNewFeeGroup => {
  return data !== undefined && 'fixed' in data && 'percentage' in data && !('fee_data' in data);
};

export interface FeeGroupAssignment {
  tempId: string;
  asset: string;
  methodId: string;
  methodName: string;
  feeGroup: FeeGroupSelection;
}

export interface SubaccountData {
  tempId: string;
  name: string;
  postbackUrl: string;
  firstname: string;
  lastname: string;
  mobileDialcode: string;
  mobile: string;
  email: string;
  businessName: string;
  feeGroup: FeeGroupSelection;
  paymentMethods: string[]; // Array of asset codes
  auth1: string; // API Key
  auth2: string; // API Secret
  isMasked: 'yes' | 'no';
  createdId?: string;
}

export interface AgentData {
  tempId: string;
  firstname: string;
  lastname: string;
  businessName: string;
  email: string;
  mobileDialcode: string;
  mobile: string;
  feeGroupAssignments: FeeGroupAssignment[];
  createdId?: string;
}

export interface PayoutMethod {
  asset: string;
  crypto: boolean;
}

export interface Asset {
  name: string;
  code: string;
  decimal: number;
  crypto: boolean;
  networks?: Array<{
    network: string;
    display_name: string;
  }>;
}

export interface Method {
  id: string;
  name: string;
}

export interface FeeGroup {
  id: string;
  name: string;
  template: {
    fixed: string;
    percentage: string;
  };
}

export interface WizardState {
  currentStep: number;
  merchant: MerchantData;
  subaccounts: SubaccountData[];
  agents: AgentData[];
}

export interface SubmissionStep {
  id: string;
  type: 'merchant' | 'subaccount' | 'agent' | 'feeGroup' | 'feeGroupAssignment' | 'paymentMethods';
  label: string;
  tempId?: string;
  dependsOn: string[];
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  error?: string;
  createdId?: string;
}

export interface SubmissionPlan {
  steps: SubmissionStep[];
  totalSteps: number;
  completedSteps: number;
  currentStepIndex: number;
  isComplete: boolean;
  hasError: boolean;
}

export const initialMerchantData: MerchantData = {
  name: '',
  description: '',
  supportEmail: '',
  restrictedIps: '',
  isBatchPostback: 'no',
  type: 'regular',
};

export const initialWizardState: WizardState = {
  currentStep: 0,
  merchant: initialMerchantData,
  subaccounts: [],
  agents: [],
};

export const generateTempId = (): string => {
  return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const createEmptySubaccount = (): SubaccountData => ({
  tempId: generateTempId(),
  name: '',
  postbackUrl: '',
  firstname: '',
  lastname: '',
  mobileDialcode: '+1',
  mobile: '',
  email: '',
  businessName: '',
  feeGroup: {
    mode: 'existing',
  },
  paymentMethods: [],
  auth1: '',
  auth2: '',
  isMasked: 'no',
});

export const createEmptyAgent = (): AgentData => ({
  tempId: generateTempId(),
  firstname: '',
  lastname: '',
  businessName: '',
  email: '',
  mobileDialcode: '',
  mobile: '',
  feeGroupAssignments: [],
});

export const createEmptyFeeGroupAssignment = (): FeeGroupAssignment => ({
  tempId: generateTempId(),
  asset: '',
  methodId: '',
  methodName: '',
  feeGroup: {
    mode: 'existing',
  },
});

// Helper to generate decimal placeholder
export const generateDecimalPlaceholder = (decimal: number): string => {
  if (decimal <= 0) return "0";
  return "0." + "0".repeat(decimal);
};

// Helper to filter numeric input
export const filterNumericInput = (value: string): string => {
  if (value === "") return "";
  let filtered = value.replace(/[^0-9.]/g, "");
  const parts = filtered.split(".");
  if (parts.length > 2) {
    filtered = parts[0] + "." + parts.slice(1).join("");
  }
  return filtered;
};

// Fee type display names
export const FEE_TYPE_DISPLAY_NAMES: Record<string, string> = {
  reseller_fee: "Agent Fee",
  provider_fee: "Provider Fee",
  payout: "Payout",
  withdrawal: "Withdrawal",
};

export const getFeeTypeDisplayName = (type: string): string => {
  return FEE_TYPE_DISPLAY_NAMES[type] || type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export const FIXED_FEE_TYPES = ["payout", "withdrawal"];
